import { useState, useEffect } from 'react';
import { stripeProductsService, StripeProduct, StripePrice, StripeFeatureItem } from '../services/stripeProducts';

export const useStripeProducts = () => {
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const productsData = await stripeProductsService.getProducts();
      setProducts(productsData);
    } catch (err: any) {
      setError(err.message || 'Error obteniendo productos');
      console.error('Error en useStripeProducts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const getProductById = (productId: string) => {
    return products.find(product => product.id === productId);
  };

  const getProductsByType = (type: string) => {
    return products.filter(product => product.metadata.type === type);
  };

  const formatPrice = (price: StripePrice) => {
    return stripeProductsService.formatPrice(price);
  };

  // Función para obtener precio por ciclo de facturación
  const getPriceByCycle = (product: StripeProduct, cycle: 'monthly' | 'yearly') => {
    return product.prices.find(price => {
      if (price.recurring) {
        return price.recurring.interval === (cycle === 'monthly' ? 'month' : 'year');
      }
      return false;
    });
  };

  // Normaliza features provenientes del backend o metadata
  const normalizeFeatures = (product: StripeProduct): StripeFeatureItem[] | undefined => {
    const rawFeatures: any = (product as any).features;

    // 1) Del backend: array de objetos { description, checked } o strings
    if (Array.isArray(rawFeatures)) {
      const normalized = rawFeatures.map((f: any) => {
        if (typeof f === 'string') {
          return { description: f, checked: true } as StripeFeatureItem;
        }
        return { description: String(f?.description ?? ''), checked: Boolean(f?.checked ?? true) } as StripeFeatureItem;
      });
      return normalized;
    }

    // 2) Desde metadata como JSON
    const json = (product.metadata?.features_json || product.metadata?.featuresJSON || '').trim();
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (Array.isArray(parsed)) {
          return parsed.map((f: any) => {
            if (typeof f === 'string') return { description: f, checked: true };
            return { description: String(f?.description ?? ''), checked: Boolean(f?.checked ?? true) };
          });
        }
      } catch (_) {}
    }

    // 3) Cadena separada por comas o saltos de línea
    const raw = (product.metadata?.features || '').trim();
    if (raw) {
      const items = raw.includes('\n') ? raw.split('\n') : raw.split(',');
      return items.map((f) => ({ description: f.trim(), checked: true }));
    }

    return undefined;
  };

  // Función para mapear productos de Stripe a planes de pricing
  const mapToPricingPlans = () => {
    return products.map(product => {
      const monthlyPrice = getPriceByCycle(product, 'monthly');
      const yearlyPrice = getPriceByCycle(product, 'yearly');
      const features = normalizeFeatures(product);
      
      return {
        id: product.id,
        name: product.name,
        description: product.description || '',
        monthlyPrice: monthlyPrice ? monthlyPrice.unit_amount / 100 : 0,
        yearlyPrice: yearlyPrice ? yearlyPrice.unit_amount / 100 : 0,
        metadata: product.metadata,
        images: product.images,
        prices: product.prices,
        features
      } as any;
    });
  };

  return {
    products,
    loading,
    error,
    refetch: fetchProducts,
    getProductById,
    getProductsByType,
    formatPrice,
    getPriceByCycle,
    mapToPricingPlans
  };
}; 